import * as React from 'react';

import { enumInventoryTransactionType } from '@rematter/frontend-lib-api-client';
import { type DataGridColumn } from '@rematter/ui-web';
import { cn } from '@rematter/ui-web/lib';
import { LucideExternalLink } from 'lucide-react';

import { type LotAllocationRecord } from '../../_graphql/queryInventoryLot';
import { useInventoryUnit } from '../../hooks/useInventoryUnit';
import { useTranslations } from '../../i18n/useTranslations';
import {
  ENTITY_PREFIX_MAP,
  parseOriginTransactionItemId,
} from '../../utils/entityLinks';

import { useModuleLocation } from '~/shared/hooks/useModuleLocation';

const formatAllocationDate = (dateStr: string): string => {
  if (!dateStr) return '-';
  const date = new Date(dateStr.replace(/\[.*\]$/, ''));
  return isNaN(date.getTime())
    ? '-'
    : date.toLocaleString(undefined, {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: 'numeric',
        minute: '2-digit',
      });
};

export const useLotAllocationColumns =
  (): DataGridColumn<LotAllocationRecord>[] => {
    const t = useTranslations();
    const [, setLocation] = useModuleLocation();
    const { formatQuantityValue } = useInventoryUnit();

    const handleOriginClick = React.useCallback(
      (originTransactionItemId: string) => {
        const parsed = parseOriginTransactionItemId(originTransactionItemId);
        if (!parsed) return;
        const config = ENTITY_PREFIX_MAP[parsed.prefix];
        if (config) {
          setLocation(config.route(parsed.parentId));
        }
      },
      [setLocation],
    );

    return React.useMemo(
      () => [
        {
          key: 'source',
          header: t.lotsListView.detail.table.columns.source,
          cell: ({ row }) => {
            const parsed = parseOriginTransactionItemId(
              row.originTransactionItemId,
            );
            const typeLabel =
              t.movementHistoryView.transactionTypes[
                row.originTransactionType
              ] || row.originTransactionType;

            if (parsed) {
              return (
                <button
                  type="button"
                  onClick={() => handleOriginClick(row.originTransactionItemId)}
                  className={cn(
                    'group flex items-center gap-1 font-medium text-primary hover:underline',
                  )}
                >
                  <span>
                    {typeLabel} #{parsed.shortId}
                  </span>
                  <LucideExternalLink className="size-3 opacity-0 transition-opacity group-hover:opacity-100" />
                </button>
              );
            }

            return <span className="font-medium">{typeLabel}</span>;
          },
        },
        {
          key: 'type',
          header: t.lotsListView.detail.table.columns.type,
          cell: ({ row }) => {
            const isTagged =
              row.originTransactionType ===
                enumInventoryTransactionType.Tagging ||
              row.originTransactionType ===
                enumInventoryTransactionType.Untagging;
            return (
              <span
                className={cn(
                  'inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium',
                  isTagged && 'bg-primary/10 text-primary',
                  !isTagged && 'bg-neutral/10 text-neutral/75',
                )}
              >
                {isTagged
                  ? t.lotsListView.detail.table.tagged
                  : t.lotsListView.detail.table.loose}
              </span>
            );
          },
        },
        {
          key: 'item',
          header: t.lotsListView.detail.table.columns.item,
          cell: ({ row }) => <span>{row.itemName}</span>,
        },
        {
          key: 'quantity',
          header: t.lotsListView.detail.table.columns.quantity,
          cell: ({ row }) => (
            <span className="tabular-nums">
              {formatQuantityValue(row.quantity, row.quantityUnit)}
            </span>
          ),
        },
        {
          key: 'date',
          header: t.lotsListView.detail.table.columns.date,
          cell: ({ row }) => (
            <span className="text-neutral/75">
              {formatAllocationDate(row.occurredAt)}
            </span>
          ),
        },
      ],
      [t, formatQuantityValue, handleOriginClick],
    );
  };
